import { useState, useCallback } from 'react';
import { AlertColor } from '@mui/material';

interface NotificationState {
    open: boolean;
    message: string;
    severity: AlertColor;
}

interface UseNotificationOptions {
    /**
     * Default duration before auto-hiding notification (in milliseconds)
     * @default 6000
     */
    defaultDuration?: number;
}

interface ShowNotificationOptions {
    /**
     * Custom duration for this specific notification (in milliseconds)
     * If not provided, uses defaultDuration from hook options
     */
    duration?: number;

    /**
     * Callback to execute after notification is shown
     * Useful for navigation after showing success message
     */
    onShow?: () => void;

    /**
     * Delay before executing onShow callback (in milliseconds)
     * Use this to ensure user sees the notification before navigation
     * @default 0
     */
    onShowDelay?: number;
}

/**
 * Custom hook for managing notification state
 *
 * @example
 * ```tsx
 * const { notification, showNotification, hideNotification } = useNotification();
 *
 * // Simple usage
 * showNotification('הטופס נשמר בהצלחה!', 'success');
 *
 * // With navigation after delay
 * showNotification('הטופס נשמר בהצלחה!', 'success', {
 *   onShowDelay: 1500,
 *   onShow: () => navigate('/home')
 * });
 * ```
 */
export const useNotification = (options: UseNotificationOptions = {}) => {
    const { defaultDuration = 6000 } = options;

    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info',
    });

    const showNotification = useCallback(
        (message: string, severity: AlertColor = 'info', showOptions?: ShowNotificationOptions) => {
            const { onShow, onShowDelay = 0 } = showOptions || {};

            setNotification({ open: true, message, severity });

            // Execute callback after delay if provided
            if (onShow) {
                if (onShowDelay > 0) {
                    setTimeout(() => {
                        onShow();
                    }, onShowDelay);
                } else {
                    onShow();
                }
            }
        },
        []
    );

    const hideNotification = useCallback(() => {
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    return {
        notification,
        showNotification,
        hideNotification,
        /**
         * Auto-hide duration for notification (in milliseconds)
         */
        autoHideDuration: defaultDuration,
    };
};
